import type { IncomeDashboardDetail } from '../api/incomeVerificationDashboardApi';

export type BankRollup = {
  creditCount: number;
  debitCount: number;
  neutralCount: number;
  creditSum: number;
  debitSum: number;
};

export function deriveBankRollup(txs: IncomeDashboardDetail['bankTransactions']): BankRollup {
  let creditCount = 0;
  let debitCount = 0;
  let neutralCount = 0;
  let creditSum = 0;
  let debitSum = 0;
  for (const t of txs ?? []) {
    const amt = t.amount ?? 0;
    if (t.direction === 'CREDIT') {
      creditCount += 1;
      creditSum += amt;
    } else if (t.direction === 'DEBIT') {
      debitCount += 1;
      debitSum += amt;
    } else {
      neutralCount += 1;
    }
  }
  return { creditCount, debitCount, neutralCount, creditSum, debitSum };
}

export type DepositEligibility = {
  eligibleCount: number;
  excludedCount: number;
  eligibleSum: number;
  excludedSum: number;
  topExclusionReasons: Array<{ reason: string; count: number }>;
};

export function deriveDepositEligibility(
  deposits: IncomeDashboardDetail['incomeDeposits'],
): DepositEligibility {
  let eligibleCount = 0;
  let excludedCount = 0;
  let eligibleSum = 0;
  let excludedSum = 0;
  const reasonCounts = new Map<string, number>();

  for (const d of deposits ?? []) {
    const amt = d.amount ?? 0;
    if (d.isEligible) {
      eligibleCount += 1;
      eligibleSum += amt;
    } else {
      excludedCount += 1;
      excludedSum += amt;
      const r = (d.exclusionReason ?? '(no reason)').trim() || '(no reason)';
      reasonCounts.set(r, (reasonCounts.get(r) ?? 0) + 1);
    }
  }

  const topExclusionReasons = [...reasonCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return { eligibleCount, excludedCount, eligibleSum, excludedSum, topExclusionReasons };
}

export type PayorGroupRow = {
  key: string;
  displayName: string;
  payorType: string;
  depositCount: number;
  sum: number;
  eligibleSum: number;
  excludedSum: number;
  dateMin: string | null;
  dateMax: string | null;
  medianGapDays: number | null;
};

function parseTime(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? NaN : t;
}

export function medianGapDaysBetweenSortedDates(dates: string[]): number | null {
  const times = dates.map(parseTime).filter((x) => !Number.isNaN(x)).sort((a, b) => a - b);
  if (times.length < 2) return null;
  const gaps: number[] = [];
  for (let i = 1; i < times.length; i += 1) {
    gaps.push((times[i] - times[i - 1]) / 86400000);
  }
  gaps.sort((a, b) => a - b);
  const mid = Math.floor(gaps.length / 2);
  return gaps.length % 2 === 1 ? gaps[mid]! : (gaps[mid - 1]! + gaps[mid]!) / 2;
}

export function derivePayorGroups(deposits: IncomeDashboardDetail['incomeDeposits']): PayorGroupRow[] {
  type Acc = {
    displayName: string;
    payorType: string;
    dates: string[];
    sum: number;
    eligibleSum: number;
    excludedSum: number;
  };
  const map = new Map<string, Acc>();

  for (const d of deposits ?? []) {
    const displayName = d.payorDisplay?.trim() || '—';
    const payorType = d.payorType ?? '—';
    const key = `${displayName}\u0000${payorType}`;
    const amt = d.amount ?? 0;
    let acc = map.get(key);
    if (!acc) {
      acc = { displayName, payorType, dates: [], sum: 0, eligibleSum: 0, excludedSum: 0 };
      map.set(key, acc);
    }
    acc.dates.push(d.depositDate);
    acc.sum += amt;
    if (d.isEligible) acc.eligibleSum += amt;
    else acc.excludedSum += amt;
  }

  const rows: PayorGroupRow[] = [];
  for (const [key, acc] of map) {
    const sortedDates = [...acc.dates].sort(
      (a, b) => parseTime(a) - parseTime(b),
    );
    const dateMin = sortedDates[0] ?? null;
    const dateMax = sortedDates[sortedDates.length - 1] ?? null;
    const medianGapDays = medianGapDaysBetweenSortedDates(acc.dates);
    rows.push({
      key,
      displayName: acc.displayName,
      payorType: acc.payorType,
      depositCount: acc.dates.length,
      sum: acc.sum,
      eligibleSum: acc.eligibleSum,
      excludedSum: acc.excludedSum,
      dateMin,
      dateMax,
      medianGapDays,
    });
  }

  return rows.sort((a, b) => b.sum - a.sum);
}

export function toCsv(headers: string[], rows: (string | number)[][]): string {
  const esc = (v: string | number) => {
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
